import { toNumber, toStringValue } from "features/shared/lib/formatters";
import { parseJsonArray } from "utils/dataManipulation";

import type { LayerArraysSource, LayerFormValue, LayerRow } from "../model/types";

const METERS_TO_MILLIMETERS = 1000;

const parseNumberArray = (raw: null | string | undefined): (number | undefined)[] =>
  parseJsonArray(raw).map((value) => (typeof value === "number" ? value : toNumber(String(value))));

export const parseMaterialNames = (raw: null | string | undefined): string[] =>
  parseJsonArray(raw).map((value) => String(value));

export const buildLayerRows = (source: LayerArraysSource): LayerRow[] => {
  const materials = parseMaterialNames(source.materialNames);
  const thicknesses = parseNumberArray(source.thicknessLayers);
  const lambdas = parseNumberArray(source.thermalConductivityLayers);
  const densities = parseNumberArray(source.densityLayers);
  const specificHeats = parseNumberArray(source.specificHeatLayers);
  const length = Math.max(materials.length, thicknesses.length, lambdas.length, densities.length, specificHeats.length);

  return Array.from({ length }, (_, index) => ({
    density: toStringValue(densities[index]),
    heatCapacity: toStringValue(specificHeats[index]),
    id: String(index),
    material: materials[index] ?? "",
    thermalConductivity: toStringValue(lambdas[index]),
    thickness: toStringValue(thicknesses[index]),
  }));
};

export const createEmptyLayer = (): LayerFormValue => ({
  density: "",
  lambda: "",
  material: "",
  specificHeat: "",
  thickness: "",
});

export const buildLayerFormValues = (source: LayerArraysSource): LayerFormValue[] => {
  const materials = parseMaterialNames(source.materialNames);
  const thicknesses = parseNumberArray(source.thicknessLayers);
  const lambdas = parseNumberArray(source.thermalConductivityLayers);
  const densities = parseNumberArray(source.densityLayers);
  const specificHeats = parseNumberArray(source.specificHeatLayers);
  const length = Math.max(materials.length, thicknesses.length, lambdas.length, densities.length, specificHeats.length);

  return Array.from({ length }, (_, index) => ({
    density: toStringValue(densities[index]),
    lambda: toStringValue(lambdas[index]),
    material: materials[index] ?? "",
    specificHeat: toStringValue(specificHeats[index]),
    thickness: toStringValue(thicknesses[index] == null ? undefined : thicknesses[index] * METERS_TO_MILLIMETERS),
  }));
};

const layerThicknessInMeters = (layer: LayerFormValue): number | undefined => {
  const millimeters = toNumber(layer.thickness);
  return millimeters == null ? undefined : millimeters / METERS_TO_MILLIMETERS;
};

export const serializeLayers = (layers: LayerFormValue[]) => ({
  densityLayers: JSON.stringify(layers.map((layer) => toNumber(layer.density) ?? 0)),
  materialNames: JSON.stringify(layers.map((layer) => layer.material)),
  specificHeatLayers: JSON.stringify(layers.map((layer) => toNumber(layer.specificHeat) ?? 0)),
  thermalConductivityLayers: JSON.stringify(layers.map((layer) => toNumber(layer.lambda) ?? 0)),
  thicknessLayers: JSON.stringify(layers.map((layer) => layerThicknessInMeters(layer) ?? 0)),
});
